// =============================================================================
// FAQ database for Prime Vision · sales-positive framing
//
// IMPORTANT: questions are written to *resolve* objections, not to plant them.
// Tone: confident, value-led, calm. No fear-mongering, no defensive answers.
// Order = priority (the FAQ section + chatbot show the first N).
//
// IT (master) + EN (translation). Keywords drive free-text matching in the
// chatbot.
// =============================================================================

export const FAQ = {
  it: [
    {
      id: 'vedere-demo',
      keywords: ['demo', 'vedere', 'mostra', 'azione', 'live', 'in funzione'],
      q: 'Posso vedere Prime Vision in azione prima di scegliere?',
      a: 'Certo. In 30 minuti ti facciamo vedere il flusso reale: visita con AI vocale, cartella clinica, agenda, follow-up, fatturazione e documentazione FSE 2.0. Non è una presentazione commerciale — è una sessione concreta sui processi del tuo studio, con un product specialist e un consulente medico.',
    },
    {
      id: 'tempi-onboarding',
      keywords: ['onboarding', 'partire', 'tempi', 'avvio', 'attivazione', 'quanto tempo'],
      q: 'Quanto rapidamente entra in produzione lo studio?',
      a: 'L\'onboarding è guidato: nei primi giorni configuriamo anagrafiche, agenda e cartella clinica con il tuo team. La maggior parte degli studi è operativa sui flussi essenziali (visita, referto, agenda) in 1–2 settimane, con migrazione progressiva del resto. Nessun blocco della normale attività dello studio.',
    },
    {
      id: 'tempo-recuperato',
      keywords: ['tempo', 'recupero', 'ore', 'risparmio', 'ritorno', 'roi'],
      q: 'Quanto tempo recupero davvero ogni settimana?',
      a: 'Dipende dal volume e dal flusso attuale, ma le testimonianze raccolte negli studi pilota indicano un risparmio medio di oltre 10 ore alla settimana fra segreteria, referti e ricerca documenti. In demo ti mostriamo dove il tuo studio sta perdendo più tempo e quantifichiamo il guadagno atteso prima che tu prenda una decisione.',
    },
    {
      id: 'sicurezza-dati',
      keywords: ['sicurezza', 'gdpr', 'privacy', 'dati', 'pazienti', 'backup', 'crittografia'],
      q: 'I miei dati e quelli dei pazienti sono al sicuro?',
      a: 'Sì. Prime Vision è progettato in modo conforme al GDPR e alle linee guida sanitarie nazionali: dati cifrati in transito e a riposo, accessi tracciati per utente, backup automatici, ruoli e permessi granulari. Il dato clinico è e resta del medico — Prime Vision lo custodisce, non lo rivende mai.',
    },
    {
      id: 'team-adozione',
      keywords: ['team', 'segreteria', 'ortottista', 'personale', 'adozione', 'metodo'],
      q: 'Prime Vision si adatta al mio team o devo cambiare metodo?',
      a: 'Si adatta. L\'interfaccia è pensata per medici, ortottisti e segreteria — chi visita davvero. Si parte dai flussi che usate già (anagrafica, visite, referti) e si digitalizza il resto a velocità del team. Nessuno deve "diventare un esperto informatico": il software lavora per voi, non il contrario.',
    },
    {
      id: 'migrazione-dati',
      keywords: ['migrazione', 'importare', 'word', 'excel', 'carta', 'storico'],
      q: 'Cosa succede ai dati che ho già su Word, Excel o carta?',
      a: 'Li recuperiamo dove utile, in modo guidato. Anagrafiche, agende e fatturato possono essere importati da Excel o da altri gestionali. Lo storico cartaceo si digitalizza progressivamente, partendo dai nuovi pazienti e dai controlli: in poche settimane lo studio ha una base digitale ordinata e pulita, senza fermare l\'attività.',
    },
    {
      id: 'ts-fse-fatturazione',
      keywords: ['fse', 'fatturazione', 'fatture', 'norma', 'compliance'],
      q: 'Come gestisce FSE 2.0 e fatturazione?',
      a: 'Prime Vision supporta un flusso FSE 2.0 a norma: la cartella clinica prepara i documenti necessari e li archivi in formato conforme. La fatturazione elettronica è integrata e ti aiuta a lavorare in modo ordinato con il percorso FSE 2.0. L\'obiettivo è togliere ore di lavoro ripetitivo alla segreteria, non aggiungerne.',
    },
    {
      id: 'pre-order-2027',
      keywords: ['pre-order', 'preorder', 'lancio', '2027', 'condizioni', 'early', 'early-adopter'],
      q: 'Cosa serve per partire con il pre-order 2027?',
      a: 'Bastano una demo gratuita di 30 minuti e una valutazione del flusso del tuo studio. Chi prenota nel pre-order entra nella prima ondata di onboarding con condizioni early-adopter bloccate (prezzo, setup prioritario, migrazione assistita). I posti sono limitati per garantire un onboarding curato studio per studio.',
    },
  ],

  en: [
    {
      id: 'vedere-demo',
      keywords: ['demo', 'see', 'action', 'live', 'in action'],
      q: 'Can I see Prime Vision in action before deciding?',
      a: 'Of course. In 30 minutes we walk you through the real flow: visit with voice AI, medical records, agenda, follow-up, billing and FSE 2.0 documentation. It\'s not a sales pitch — it\'s a concrete session on your practice\'s processes, with a product specialist and a medical consultant.',
    },
    {
      id: 'tempi-onboarding',
      keywords: ['onboarding', 'start', 'time', 'activation', 'how long'],
      q: 'How fast does the practice go live?',
      a: 'Onboarding is guided. In the first few days we configure patient records, agenda and the medical chart with your team. Most practices are operational on the essential flows (visit, report, agenda) in 1–2 weeks, with progressive migration of the rest. No disruption to daily activity.',
    },
    {
      id: 'tempo-recuperato',
      keywords: ['time', 'hours', 'recover', 'savings', 'roi'],
      q: 'How much time do I actually recover every week?',
      a: 'It depends on volume and current flow, but pilot practices consistently report saving more than 10 hours per week between front-desk, reports and document searches. In the demo we show you where your practice is losing the most time and we quantify the expected gain before you decide.',
    },
    {
      id: 'sicurezza-dati',
      keywords: ['security', 'gdpr', 'privacy', 'data', 'patients', 'backup'],
      q: 'Are my data and patient data safe?',
      a: 'Yes. Prime Vision is designed to be GDPR-compliant and aligned with Italian healthcare guidelines: data encrypted in transit and at rest, per-user audit trail, automatic backups, granular roles and permissions. Clinical data stays the doctor\'s — Prime Vision custodies it, it never resells it.',
    },
    {
      id: 'team-adozione',
      keywords: ['team', 'front desk', 'orthoptist', 'staff', 'adoption'],
      q: 'Does Prime Vision adapt to my team, or do I need to change method?',
      a: 'It adapts. The interface is designed for doctors, orthoptists and front-desk — people who actually examine patients. We start from the flows you already use (records, visits, reports) and digitize the rest at your team\'s pace. Nobody needs to "become a tech expert": the software works for you, not the other way around.',
    },
    {
      id: 'migrazione-dati',
      keywords: ['migration', 'import', 'word', 'excel', 'paper', 'history'],
      q: 'What happens to data I already have in Word, Excel or on paper?',
      a: 'We recover what\'s useful, in a guided way. Patient records, agendas and revenue can be imported from Excel or other systems. The paper archive is digitised progressively, starting with new patients and follow-ups: in a few weeks your practice has a clean, ordered digital base — without halting activity.',
    },
    {
      id: 'ts-fse-fatturazione',
      keywords: ['fse', 'billing', 'invoicing', 'compliance'],
      q: 'How does it handle FSE 2.0 and invoicing?',
      a: 'Prime Vision supports a compliant FSE 2.0 flow: the medical chart prepares the required documents and you archive them in a compliant format. E-invoicing is built in and helps you work in an ordered way with the FSE 2.0 process. The goal is to remove repetitive front-desk work, not to add to it.',
    },
    {
      id: 'pre-order-2027',
      keywords: ['pre-order', 'preorder', 'launch', '2027', 'terms', 'early'],
      q: 'What does it take to join the 2027 pre-order?',
      a: 'A free 30-minute demo and a quick assessment of your practice\'s workflow. Pre-order practices join the first wave of onboarding with locked early-adopter terms (price, priority setup, assisted migration). Seats are limited to guarantee curated, practice-by-practice onboarding.',
    },
  ],
};

export const WELCOME = {
  it: 'Ciao! Sono l\'assistente AI di Prime Vision. Chiedimi pure: adozione, valore clinico, formazione, sicurezza, integrazioni, costi, lancio 2027… Risponderò in tempo reale, oppure scrivici su WhatsApp.',
  en: 'Hi! I\'m the Prime Vision AI assistant. Ask me anything: adoption, clinical value, training, security, integrations, costs, 2027 launch… I\'ll answer in real time, or message us on WhatsApp.',
};

// Free-text matcher: returns the best matching FAQ (by keyword overlap)
// or null if nothing relevant is found.
export function matchFAQ(query, locale = 'it') {
  if (!query || !query.trim()) return null;
  const q = query.toLowerCase().normalize('NFKD');
  const list = FAQ[locale] || FAQ.it;
  let best = null;
  let bestScore = 0;
  for (const item of list) {
    let score = 0;
    for (const kw of item.keywords) {
      if (q.includes(kw.toLowerCase())) score += kw.length;
    }
    // exact question containment is a strong boost
    if (q.includes(item.q.toLowerCase().slice(0, 18))) score += 30;
    if (score > bestScore) { bestScore = score; best = item; }
  }
  return bestScore >= 4 ? best : null;
}
